import { Link } from 'react-router-dom';
import {
  ArrowRight, BadgeCheck, Briefcase, Building2, Calendar, Check,
  CheckCircle2, FileText, Handshake, GraduationCap, MapPin,
  MessageSquare, Send, ShieldCheck, Sparkles, Trophy, Users,
} from 'lucide-react';
import { CTAPair, FaqAccordion, Notice } from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /for-partners — "Two sides of every action" split-screen format.
 *
 * The reader is one of six partner types (company, training provider,
 * certification body, mentor, hackathon host, program/bootcamp). Every
 * section shows what the PARTNER does on the left and what the STUDENT
 * sees on the right — same event, two perspectives — literally
 * demonstrating Kollez's "one register both sides can read" claim.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (for-partners gets the split-screen "your side / student's side" format).
 */

/* ────────── sub-components ────────── */

function SplitFrame({
  left, right, event,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
  event: { time: string; action: string };
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:gap-8 items-stretch">
      {/* Partner side (light) */}
      <div
        className="rounded-lg overflow-hidden card"
        style={{ position: 'relative' }}
      >
        <div
          className="px-4 py-2 text-[10px] uppercase tracking-widest font-medium"
          style={{
            background: 'rgb(var(--bg-secondary))',
            color: 'rgb(var(--ink-tertiary))',
            borderBottom: '1px solid rgb(var(--line-tertiary))',
          }}
        >
          You · partner
        </div>
        <div className="p-5">{left}</div>
      </div>

      {/* Event connector — desktop only */}
      <div
        className="hidden lg:flex flex-col items-center justify-center gap-2 px-2"
        aria-hidden="true"
      >
        <div
          className="flex items-center justify-center rounded-full font-semibold"
          style={{
            width: 44, height: 44,
            background: 'rgb(var(--ink-accent))',
            color: 'rgb(var(--ink-on-accent))',
            fontSize: 12,
            letterSpacing: '0.05em',
          }}
        >
          <ArrowRight size={20} />
        </div>
        <div
          className="text-[10px] uppercase tracking-widest text-center"
          style={{ color: 'rgb(var(--ink-tertiary))', letterSpacing: '0.15em' }}
        >
          {event.time}
        </div>
        <div
          className="text-[10px] text-center max-w-[80px]"
          style={{ color: 'rgb(var(--ink-secondary))', lineHeight: 1.3 }}
        >
          {event.action}
        </div>
      </div>

      {/* Student side (dark inkwell) */}
      <div
        className="rounded-lg overflow-hidden inkwell"
        style={{ position: 'relative' }}
      >
        <div
          className="px-4 py-2 text-[10px] uppercase tracking-widest font-medium"
          style={{
            background: 'rgba(255,255,255,0.05)',
            color: 'rgba(255,255,255,0.55)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          Student · what they see
        </div>
        <div className="p-5" style={{ color: 'rgba(255,255,255,0.92)' }}>{right}</div>
      </div>
    </div>
  );
}

function SectionHead({ n, eyebrow, title, subtitle }: {
  n: string; eyebrow: string; title: string; subtitle: string;
}) {
  return (
    <div className="grid gap-3 mb-8 max-w-3xl">
      <div className="flex items-baseline gap-3">
        <span
          className="font-serif tabular-nums"
          style={{ fontSize: '0.9rem', color: 'rgb(var(--ink-accent))', minWidth: 32 }}
        >
          {n}
        </span>
        <p className="eyebrow m-0">{eyebrow}</p>
      </div>
      <h2
        className="text-ink-primary ml-9"
        style={{
          fontFamily: 'Newsreader, Georgia, serif',
          fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
          lineHeight: 1.15,
          fontWeight: 500,
          letterSpacing: '-0.005em',
          margin: 0,
        }}
      >
        {title}
      </h2>
      <p className="text-ink-secondary m-0 ml-9" style={{ maxWidth: '58ch' }}>
        {subtitle}
      </p>
    </div>
  );
}

/* ────────── page ────────── */

export function ForPartnersPage() {
  return (
    <>
      <PageMeta
        title="For partners"
        description="One register, two sides. You post; students see. Every partner action on Kollez creates a matching student event — same drive, two perspectives, always in sync."
        canonical="/for-partners"
      />

      {/* HERO — split panels teaser */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 pt-16 lg:pt-24 pb-8 grid gap-8">
        <div className="max-w-3xl">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            For partners · two sides of every action
          </p>
          <h1
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            One register.<br />
            You post — students see.
          </h1>
          <p className="mt-6 text-lg leading-8 text-ink-secondary m-0" style={{ maxWidth: '62ch' }}>
            This page is not a features grid. It shows what happens
            when you take an action — on your side and on the
            student&apos;s side — in the same moment, in the same
            register. Four events. Two sides. Every one, always in
            sync.
          </p>
          <div className="mt-8">
            <CTAPair
              primary={{ label: 'Post your first opportunity — free', to: '/register/partner' }}
              secondary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
            />
          </div>
          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-secondary" aria-label="Trust signals">
            <li className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-ink-success" />Verified employer program</li>
            <li className="flex items-center gap-1.5"><Sparkles size={14} className="text-ink-accent" />Nexie ranks — cited, human-approved</li>
            <li className="flex items-center gap-1.5"><MapPin size={14} className="text-ink-success" />Contact hidden until shortlist</li>
          </ul>
        </div>
      </section>

      {/* EVENT 01 — Post a job */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-14 lg:py-20 border-t border-line-tertiary">
        <SectionHead
          n="01"
          eyebrow="You post a job"
          title="The drive lands in every eligible student's feed. In seconds."
          subtitle="No email blast. No campus travel. You publish once; every eligible student on any partner campus in your network sees it, filtered by their DigiLocker-verified marks."
        />
        <SplitFrame
          event={{ time: '10:00 AM', action: 'Post published' }}
          left={
            <div className="grid gap-3 text-sm">
              <div className="flex items-center justify-between gap-2 pb-2 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
                <div className="flex items-center gap-2">
                  <span className="logo-tile" style={{ width: 32, height: 32 }}>W</span>
                  <div>
                    <div className="font-medium">Wipro NTH · Project Engineer</div>
                    <div className="text-xs text-ink-tertiary">Draft · about to publish</div>
                  </div>
                </div>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                  style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
                >
                  Day 1 tier
                </span>
              </div>
              <div className="grid gap-2 text-xs">
                {[
                  ['Eligibility', 'CGPA ≥ 7.5 · CSE, IT, ECE · no active backlogs'],
                  ['Package', '₹6.5 LPA · 24 Sept drive date'],
                  ['Campuses', '7 partner campuses (auto-select from your MoU)'],
                  ['Nexie', 'Drafted the JD. Filter suggested from past hires.'],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[90px_1fr] gap-2 items-baseline">
                    <span className="text-[10px] uppercase tracking-wider text-ink-tertiary">{k}</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-primary btn-sm w-full mt-1"
                style={{ padding: '0.5rem 0.75rem' }}
              >
                Publish to 7 campuses
              </button>
            </div>
          }
          right={
            <div className="grid gap-3 text-sm">
              <div className="flex items-center gap-2 pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span
                  className="flex items-center justify-center rounded-full font-semibold text-xs"
                  style={{ width: 26, height: 26, background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)' }}
                >
                  MV
                </span>
                <div className="text-xs">Meghana's phone · 10:02 AM</div>
              </div>
              <div
                className="rounded-lg p-3 text-xs"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider"
                    style={{ background: 'rgba(120,200,140,0.18)', color: 'rgb(160,220,180)' }}
                  >
                    New · Day 1
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.55)' }}>You&apos;re eligible</span>
                </div>
                <div className="font-medium" style={{ color: '#fff' }}>Wipro NTH · Project Engineer</div>
                <div className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  24 Sept · ₹6.5 LPA · 7 campuses
                </div>
                <div
                  className="mt-2 pt-2 text-[11px]"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.08)', color: 'rgb(160,220,180)' }}
                >
                  ✓ CGPA 8.7 ≥ 7.5 · CSE ✓ · No backlogs ✓
                </div>
              </div>
              <div
                className="text-[10px] italic"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                Landed in 1,240 eligible students' feeds simultaneously.
              </div>
            </div>
          }
        />
      </section>

      {/* EVENT 02 — Nexie shortlist */}
      <section
        className="py-14 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10">
          <SectionHead
            n="02"
            eyebrow="Nexie ranks the shortlist"
            title="You approve. Never a black box."
            subtitle="Nexie scores fit against your posting — marks, portable badges, past projects, similarity to your last hires — and shows the reason next to every rank. You approve before any candidate is contacted."
          />
          <SplitFrame
            event={{ time: '4:00 PM', action: 'Shortlist reviewed' }}
            left={
              <div className="grid gap-3 text-sm">
                <div className="flex items-center justify-between gap-2 pb-2 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
                  <div className="flex items-center gap-2">
                    <span
                      className="flex items-center justify-center rounded-md"
                      style={{ width: 24, height: 24, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
                    >
                      <Sparkles size={12} />
                    </span>
                    <div>
                      <div className="font-medium text-xs">Nexie · shortlist for Wipro NTH</div>
                      <div className="text-[10px] text-ink-tertiary">412 applicants · top 40 recommended</div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-2">
                  {[
                    { initials: 'MV', name: 'Meghana Varma', score: 94, why: 'CGPA 8.7 · Python + SQL verified · past project matches JD' },
                    { initials: 'AR', name: 'Aisha Reddy',   score: 89, why: 'CGPA 8.4 · System Design L2 · 2 internships at target scale' },
                    { initials: 'HT', name: 'Harish T.',      score: 86, why: 'CGPA 8.1 · matches your last 3 hires\' profile' },
                  ].map((c) => (
                    <div
                      key={c.initials}
                      className="rounded p-2 grid gap-1"
                      style={{ background: 'rgb(var(--bg-secondary))' }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="flex items-center justify-center rounded-full text-xs font-semibold"
                          style={{ width: 24, height: 24, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
                        >
                          {c.initials}
                        </span>
                        <div className="text-xs font-medium flex-1">{c.name}</div>
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                          style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-success))' }}
                        >
                          {c.score}
                        </span>
                      </div>
                      <p className="helper m-0 text-[10px] ml-8">{c.why}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-1">
                  <button
                    className="btn btn-primary text-xs flex-1"
                    style={{ padding: '0.4rem 0.6rem' }}
                  >
                    Approve top 40
                  </button>
                  <button
                    className="btn btn-outline text-xs"
                    style={{ padding: '0.4rem 0.6rem' }}
                  >
                    Adjust
                  </button>
                </div>
              </div>
            }
            right={
              <div className="grid gap-3 text-sm">
                <div
                  className="rounded-lg p-3 text-xs"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <div
                    className="text-[10px] uppercase tracking-widest mb-2"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                  >
                    Application status
                  </div>
                  <div className="font-medium" style={{ color: '#fff' }}>Wipro NTH · Project Engineer</div>
                  <div
                    className="mt-2 flex items-center gap-2 text-[11px] font-medium"
                    style={{ color: 'rgb(160,220,180)' }}
                  >
                    <CheckCircle2 size={12} /> Shortlisted for interview
                  </div>
                </div>
                <div
                  className="rounded-lg p-3 text-xs"
                  style={{ background: 'rgba(255,255,255,0.05)' }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles size={11} style={{ color: 'rgb(232,212,122)' }} />
                    <span
                      className="text-[10px] uppercase tracking-widest font-medium"
                      style={{ color: 'rgb(232,212,122)' }}
                    >
                      Why you were picked
                    </span>
                  </div>
                  <p className="m-0" style={{ color: 'rgba(255,255,255,0.85)' }}>
                    Your CGPA (8.7) and your verified SQL and Python badges match this posting&apos;s eligibility. Your Airflow project overlapped with the JD keywords.
                  </p>
                  <p
                    className="m-0 mt-1 text-[10px]"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                  >
                    Contact stays hidden until you accept the interview slot.
                  </p>
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* EVENT 03 — Interview slot */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-14 lg:py-20 border-t border-line-tertiary">
        <SectionHead
          n="03"
          eyebrow="You open 40 interview slots"
          title="Students pick. WhatsApp reminders go out. No spreadsheet."
          subtitle="Post your interview window. Kollez shares your calendar with the 40 shortlisted candidates. They pick a slot on their phone. WhatsApp reminders 24 hours and 1 hour before."
        />
        <SplitFrame
          event={{ time: '11:00 AM', action: 'Slots opened' }}
          left={
            <div className="grid gap-3 text-sm">
              <div className="flex items-center gap-2 pb-2 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
                <Calendar size={14} style={{ color: 'rgb(var(--ink-accent))' }} />
                <div className="text-xs">
                  <div className="font-medium">Interview schedule · 27 Sept</div>
                  <div className="text-[10px] text-ink-tertiary">40 slots · 09:00 – 18:00 · online (Meet)</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 text-[10px] text-center">
                {Array.from({ length: 24 }).map((_, i) => {
                  const time = 9 + Math.floor(i / 2);
                  const half = i % 2 === 0 ? '00' : '30';
                  const taken = [2, 5, 6, 9, 11, 14, 15, 17, 20, 22].includes(i);
                  return (
                    <div
                      key={i}
                      className="rounded px-1.5 py-1 tabular-nums"
                      style={{
                        background: taken ? 'rgb(var(--bg-info))' : 'rgb(var(--bg-secondary))',
                        color: taken ? 'rgb(var(--ink-accent))' : 'rgb(var(--ink-tertiary))',
                        fontWeight: taken ? 600 : 400,
                      }}
                    >
                      {time}:{half}
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2 text-[10px] text-ink-tertiary">
                <div><b className="text-ink-primary font-medium">10</b> · slots picked</div>
                <div><b className="text-ink-primary font-medium">30</b> · open</div>
                <div><b className="text-ink-primary font-medium">40</b> · candidates</div>
              </div>
            </div>
          }
          right={
            <div className="grid gap-3 text-sm">
              <div
                className="rounded-lg p-3 text-xs flex items-center gap-2"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <MessageSquare size={16} style={{ color: 'rgb(160,220,180)' }} />
                <div>
                  <div className="font-medium" style={{ color: '#fff' }}>Interview time?</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)' }}>Pick a slot with Wipro · 27 Sept</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-[10px] text-center">
                {['10:00', '10:30', '11:00', '11:30', '12:00', '14:00'].map((t, i) => (
                  <div
                    key={t}
                    className="rounded px-1.5 py-2 tabular-nums font-medium"
                    style={{
                      background: i === 1 ? 'rgb(232,212,122)' : 'rgba(255,255,255,0.05)',
                      color: i === 1 ? 'rgb(31,64,175)' : 'rgba(255,255,255,0.85)',
                    }}
                  >
                    {t}
                  </div>
                ))}
              </div>
              <div
                className="text-[10px] italic"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                WhatsApp reminders 24 hrs and 1 hr before.
              </div>
            </div>
          }
        />
      </section>

      {/* EVENT 04 — Offer + accept */}
      <section
        className="py-14 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10">
          <SectionHead
            n="04"
            eyebrow="You release an offer"
            title="Sign-off checked. Signed on the phone. Ten-minute undo."
            subtitle="Kollez runs four automated checks before every offer release — name-match, CTC-in-band, authorised signatory, no double-placement. The student signs on their phone. Ten-minute undo either way."
          />
          <SplitFrame
            event={{ time: '2:00 PM', action: 'Offer released' }}
            left={
              <div className="grid gap-3 text-sm">
                <div className="flex items-baseline justify-between gap-2 pb-2 border-b" style={{ borderColor: 'rgb(var(--line-tertiary))' }}>
                  <div>
                    <div className="text-xs text-ink-tertiary">Offer letter · draft</div>
                    <div className="font-medium">Wipro NTH · Project Engineer · Meghana Varma</div>
                  </div>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                    style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-success))' }}
                  >
                    Ready
                  </span>
                </div>
                <div className="grid gap-1.5">
                  {[
                    'Name matches roster',
                    'CTC within band (₹4–12 LPA)',
                    'Signatory authorised',
                    'No double-placement',
                  ].map((c) => (
                    <div key={c} className="flex items-center gap-2 text-xs">
                      <Check size={12} style={{ color: 'rgb(var(--ink-success))' }} />
                      <span>{c}</span>
                    </div>
                  ))}
                </div>
                <button
                  className="btn btn-primary text-xs w-full mt-1"
                  style={{ padding: '0.5rem 0.75rem' }}
                >
                  Release offer
                </button>
                <div className="text-[10px] text-ink-tertiary italic">
                  Signed by Kollez · full audit chain attached
                </div>
              </div>
            }
            right={
              <div className="grid gap-3 text-sm">
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ background: '#faf6ea', color: '#1a1d2b', boxShadow: '0 0 0 6px rgba(255, 248, 234, 0.05)' }}
                >
                  <div
                    className="px-3 py-2 flex items-center gap-2"
                    style={{ background: 'rgba(21,115,74,0.08)', color: 'rgb(21,115,74)' }}
                  >
                    <BadgeCheck size={14} />
                    <span className="text-xs font-medium">Offer letter · Wipro</span>
                  </div>
                  <div className="p-3">
                    <div className="font-serif text-sm leading-tight">Congratulations, Meghana Varma.</div>
                    <div className="text-[11px] mt-1" style={{ color: '#5b6070' }}>
                      Project Engineer · ₹6.5 LPA · 24 Sept 2026 start
                    </div>
                    <button
                      className="btn btn-primary w-full text-xs mt-3"
                      style={{ padding: '0.4rem 0.6rem' }}
                    >
                      Accept on my phone
                    </button>
                  </div>
                </div>
                <div
                  className="text-[10px] italic"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                >
                  10-minute undo on both sides after accept.
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* SIX OFFERING TYPES — the six audiences as a grid */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 border-t border-line-tertiary">
        <div className="max-w-3xl mb-8">
          <p className="eyebrow flex items-center gap-1.5">
            <Users size={12} />
            Not just jobs
          </p>
          <h2
            className="text-ink-primary mt-2"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.15,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              margin: 0,
            }}
          >
            Six kinds of partner. Same split-screen.
          </h2>
          <p className="text-ink-secondary m-0 mt-3" style={{ maxWidth: '58ch' }}>
            Post anything a student can pick — job, training, certification,
            hackathon, mentorship, program. The same you-post / student-sees
            rhythm holds across every offering type.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: <Briefcase size={18} />,      name: 'Companies',            hint: 'Full-time roles + internships for graduating batches' },
            { icon: <GraduationCap size={18} />,  name: 'Training providers',   hint: 'Free or paid cohorts, live or self-paced' },
            { icon: <BadgeCheck size={18} />,     name: 'Certification bodies', hint: 'Issued badges students carry across roles' },
            { icon: <Handshake size={18} />,      name: 'Mentors',              hint: '1:1 or cohort, career or skill-focused' },
            { icon: <Trophy size={18} />,         name: 'Hackathon hosts',      hint: 'Weekend, week-long, campus or online' },
            { icon: <FileText size={18} />,       name: 'Programs & bootcamps', hint: 'Structured tracks with completion outcomes' },
          ].map((t) => (
            <article key={t.name} className="card card-pad grid gap-3">
              <span
                className="flex items-center justify-center rounded-lg"
                style={{ width: 40, height: 40, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
              >
                {t.icon}
              </span>
              <h3 className="font-serif text-lg m-0">{t.name}</h3>
              <p className="helper m-0">{t.hint}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ATS + INTEGRATIONS + ETHICS strip */}
      <section
        className="py-16 lg:py-20 border-t border-line-tertiary"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 grid gap-10 lg:grid-cols-2 lg:gap-14">
          {/* ATS */}
          <div>
            <p className="eyebrow m-0 text-xs">Works with your ATS</p>
            <h2 className="h-serif text-2xl mt-2 m-0">Export shortlists to your existing tracker.</h2>
            <p className="mt-3 text-ink-secondary m-0" style={{ maxWidth: '52ch' }}>
              Kollez talks to the ATSes most Indian recruiters already run.
              CSV export from every screen; deep integration on paid tiers.
            </p>
            <div className="mt-5 grid gap-2 grid-cols-2 sm:grid-cols-4">
              {['Darwinbox', 'Zoho Recruit', 'Workday', 'Greenhouse', 'SAP SF', 'HrBluSky', 'Naukri RMS', 'Freshteam'].map((a) => (
                <div
                  key={a}
                  className="rounded-md px-2.5 py-2 text-xs text-center"
                  style={{ background: 'rgb(var(--bg-primary))', border: '1px solid rgb(var(--line-tertiary))', fontFamily: 'Newsreader, Georgia, serif', fontStyle: 'italic', color: 'rgb(var(--ink-secondary))' }}
                >
                  {a}
                </div>
              ))}
            </div>
            <p className="helper mt-3">
              Not on the list?{' '}
              <Link className="link" to="/contact?topic=integrations">Ask us</Link>{' '}
              — CSV works with everything meanwhile.
            </p>
          </div>

          {/* Ethics */}
          <div>
            <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
              <ShieldCheck size={12} />
              Rules that protect the students you hire
            </p>
            <h2 className="h-serif text-2xl mt-2 m-0">Three rules. If you can&apos;t live with them, you can&apos;t be a verified partner.</h2>
            <ol className="mt-5 grid gap-3">
              {[
                { t: 'Never charge students', b: 'No application fee, no interview fee, no training fee to be considered for placement.' },
                { t: 'Real offerings only',   b: 'Every listing is a real opportunity a real student can win. No "talent pool" bait.' },
                { t: 'No PII harvesting',     b: 'Contact info revealed after shortlist only. Data you export is scoped to the drive.' },
              ].map((r, i) => (
                <li key={r.t} className="grid grid-cols-[28px_1fr] gap-3 items-baseline">
                  <span
                    className="flex items-center justify-center rounded-full font-semibold"
                    style={{ width: 26, height: 26, background: 'rgb(var(--bg-danger))', color: 'rgb(var(--ink-danger))', fontSize: 12 }}
                  >
                    {i + 1}
                  </span>
                  <div>
                    <div className="font-medium text-sm">{r.t}</div>
                    <p className="helper m-0 mt-1">{r.b}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-5">
              <Notice tone="info">
                Verified partners pledge to these on onboarding. Any breach ends the partnership.{' '}
                <Link className="link" to="/contact?topic=fraud">Report a violation</Link>.
              </Notice>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING + COMPLIANCE STRIP */}
      <section className="mx-auto max-w-page-wide px-5 lg:px-10 py-14 lg:py-16">
        <div className="grid gap-4 md:grid-cols-2">
          <Link to="/pricing?aud=partners" className="card card-pad clickable flex items-center gap-4 flex-wrap">
            <span
              className="flex items-center justify-center rounded-lg flex-shrink-0"
              style={{ width: 44, height: 44, background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-success))' }}
            >
              <Sparkles size={20} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg leading-tight">Free from partner campuses</div>
              <p className="helper m-0 mt-1">Post any offering to campuses in your MoU. Unlimited.</p>
            </div>
            <span className="text-sm text-ink-accent font-medium">See pricing →</span>
          </Link>
          <Link to="/pricing?aud=partners" className="card card-pad clickable flex items-center gap-4 flex-wrap">
            <span
              className="flex items-center justify-center rounded-lg flex-shrink-0"
              style={{ width: 44, height: 44, background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-accent))' }}
            >
              <Building2 size={20} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg leading-tight">Cross-campus reach</div>
              <p className="helper m-0 mt-1">Reach students outside your MoU network. Priced per campus visit.</p>
            </div>
            <span className="text-sm text-ink-accent font-medium">Talk to us →</span>
          </Link>
        </div>
        <div className="mt-6 grid gap-3 grid-cols-2 sm:grid-cols-4 text-sm">
          {[
            [<ShieldCheck size={14} key="1" />, 'SOC 2 · ISO 27001'],
            [<Users size={14} key="2" />, 'DPDP Act 2023'],
            [<MapPin size={14} key="3" />, 'Hosted in India'],
            [<BadgeCheck size={14} key="4" />, 'Verified employer'],
          ].map(([icon, title], i) => (
            <div key={i} className="flex items-center gap-2">
              <span style={{ color: 'rgb(var(--ink-success))' }}>{icon}</span>
              <span className="font-medium">{title}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL — inkwell pull quote */}
      <section
        className="py-16 lg:py-20"
        style={{ background: 'rgb(var(--bg-secondary))' }}
      >
        <div className="mx-auto max-w-page-narrow px-5 lg:px-10">
          <div className="inkwell rounded-lg p-8 lg:p-10 grid gap-4">
            <p
              className="font-serif italic m-0"
              style={{
                fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                lineHeight: 1.35,
                color: 'rgba(255,255,255,0.92)',
                textWrap: 'pretty' as never,
              }}
            >
              &ldquo;One post reached every partner campus. Time-to-hire dropped from six weeks to three days, and every candidate&apos;s marks were already verified before we saw them.&rdquo;
            </p>
            <div className="text-sm mt-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
              — Priya Ramesh, Campus TA lead · Wipro Elite NTH
            </div>
            <div className="text-xs font-medium" style={{ color: 'rgb(232,212,122)' }}>
              6 weeks → 3 days
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-line-tertiary py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-10">
          <FaqAccordion
            heading="Common questions"
            emitJsonLd
            items={[
              { q: 'How is candidate quality assured?',
                a: 'Every student profile is verified against college roster + DigiLocker marks + Kollez assessment history. Portable badges show what they\'ve actually done — not what they claim.' },
              { q: 'How does Nexie rank candidates?',
                a: 'Nexie scores fit against your posting using marks, portable badges, past projects, and how similar candidates you\'ve hired before performed. Every score comes with a plain-English reason. You approve the shortlist before any candidate is contacted.' },
              { q: 'What does "free from partner campuses" cover?',
                a: 'Any offering (job, internship, training, certification, hackathon, mentorship) to any student at a campus you have an MoU with. Unlimited posts. No per-hire fee. Cross-campus is priced per campus.' },
              { q: 'How does verification work?',
                a: 'We check company registration, domain ownership, LinkedIn presence, and a signed ethics pledge. Verification typically takes one working day. You can post drafts immediately; they publish after verification.' },
              { q: 'What ATS do you integrate with?',
                a: 'Darwinbox, Zoho Recruit, Workday, Greenhouse, SAP SuccessFactors, HrBluSky, Naukri RMS, Freshteam. Not on the list? CSV export from every screen — one click.' },
              { q: 'Can training providers and mentors also use Kollez?',
                a: 'Yes. Training providers, certification bodies, individual mentors, and hackathon hosts all sign up through /register/partner. Same verification, same rules.' },
              { q: 'What data can I export?',
                a: 'Shortlists, application status, feedback rounds — anything scoped to your drives. Full CSV/JSON export. Anything outside your drive scope stays private to the student.' },
              { q: 'What happens if I want to leave?',
                a: 'No lock-in. Export your data, we archive our copy after 60 days. You keep the badges you issued; students keep them permanently regardless.' },
            ]}
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20 text-center">
        <h2 className="display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          Post your first opportunity today.
        </h2>
        <p className="mt-4 text-ink-secondary max-w-xl mx-auto">
          Free from partner campuses. Verified in one working day. First eligible candidates the same week.
        </p>
        <div className="mt-8 flex justify-center">
          <CTAPair
            primary={{ label: 'Post your first opportunity — free', to: '/register/partner' }}
            secondary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
            align="center"
          />
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="sticky-action">
        <Link to="/register/partner" className="btn btn-primary btn-lg w-full">
          Post your first opportunity — free
        </Link>
      </div>
    </>
  );
}
